<template>
  <div class="locations-uservisits">
    <h2>{{ $t('views.locations.uservisits.title') }}</h2>
    <p class="text-muted mb-3">{{ $t('views.locations.uservisits.description') }}</p>

    <b-card class="uservisits-filter-card mb-3" body-class="py-2 px-2">
      <template #header>
        <span class="uservisits-filter-card__title">{{ $t('views.locations.uservisits.filter.title') }}</span>
      </template>

      <b-form @submit.prevent="resetAndFetch">
        <b-row class="align-items-end uservisits-filter-row">
          <b-col cols="12" sm="auto" class="mb-2 mb-sm-0 pr-sm-2">
            <b-form-group
              :label="$t('views.locations.uservisits.filter.from')"
              label-for="uservisits-filter-from"
              label-class="uservisits-filter-label"
              class="mb-0"
            >
              <b-form-datepicker
                id="uservisits-filter-from"
                v-model="filters.from"
                size="sm"
                :start-weekday="$store.getters['gui/getDatetimeWeekStartsOn']"
                :hide-header="true"
                :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                :locale="$store.getters['gui/getLanguage']"
                class="uservisits-filter-datepicker"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" sm="auto" class="mb-2 mb-sm-0 pr-sm-2">
            <b-form-group
              :label="$t('views.locations.uservisits.filter.to')"
              label-for="uservisits-filter-to"
              label-class="uservisits-filter-label"
              class="mb-0"
            >
              <b-form-datepicker
                id="uservisits-filter-to"
                v-model="filters.to"
                size="sm"
                :start-weekday="$store.getters['gui/getDatetimeWeekStartsOn']"
                :hide-header="true"
                :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                :locale="$store.getters['gui/getLanguage']"
                class="uservisits-filter-datepicker"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" sm="auto" class="mb-0">
            <b-button
              type="submit"
              variant="primary"
              size="sm"
              :disabled="isLoading"
              class="uservisits-filter-submit"
            >
              <b-spinner v-if="isLoading" small class="mr-1" />
              {{ $t('views.locations.uservisits.filter.apply') }}
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-card>

    <div>
      <b-table
        :items="items"
        :fields="fields"
        striped
        hover
        show-empty
      >
      <template #cell(created)="data">
        {{ formatDate(data.value) }}
      </template>
      </b-table>
    </div>

    <div class="mt-3 d-flex flex-wrap align-items-center">
      <b-button
        variant="outline-primary"
        size="sm"
        :disabled="isLoading || !hasMore"
        class="mr-3 mb-2"
        @click="loadMore"
      >
        {{ $t('views.locations.uservisits.loadmore') }}
      </b-button>
      <span v-if="isLoading" class="text-muted mb-2">{{ $t('views.locations.uservisits.loading') }}</span>
    </div>
  </div>
</template>

<script>
import MixinEntityBase from '@/components/mixins/EntityBase'
import http from '@/$plugins/http/core'
import { STOREMODULE_ENDPOINTMODULE_MAP, STOREMODULE_ENDPOINT_MAP } from '@/constants'

export default {
  name: 'Locations.UserVisits',
  mixins: [MixinEntityBase],
  data () {
    return {
      items: [],
      fields: [
        { key: 'created', label: 'Datum' },
        { key: 'phone', label: 'Telefonnummer' },
        { key: 'email', label: 'Email' },
        { key: 'userId', label: 'User ID' }
      ],
      filters: {
        from: null,
        to: null
      },
      page: 1,
      pageSize: 100,
      cursorCreated: null,
      hasMore: true,
      isLoading: false
    }
  },
  mounted () {
    this.initDefaultFilters()
    this.resetAndFetch()
  },
  methods: {
    initDefaultFilters () {
      const to = new Date()
      to.setHours(23, 59, 59, 999)

      const from = new Date(to)
      from.setDate(from.getDate() - 30)
      from.setHours(0, 0, 0, 0)

      this.filters.from = from
      this.filters.to = to
    },
    /**
     * b-form-datepicker liefert je nach Konfiguration einen String (z.B. YYYY-MM-DD) oder ein Date.
     */
    parseFilterDate (value) {
      if (value == null || value === '') return null
      if (value instanceof Date) {
        return isNaN(value.getTime()) ? null : new Date(value.getTime())
      }
      if (typeof value === 'string') {
        const s = value.trim()
        const ymdOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
        if (ymdOnly) {
          return new Date(+ymdOnly[1], +ymdOnly[2] - 1, +ymdOnly[3])
        }
        const d = new Date(s)
        return isNaN(d.getTime()) ? null : d
      }
      return null
    },
    toApiDateTime (date) {
      if (!date) return null
      const pad = (n) => String(n).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    },
    buildFetchUrl () {
      const baseUrl = `/${STOREMODULE_ENDPOINTMODULE_MAP.customer}/api/${STOREMODULE_ENDPOINT_MAP.userlocationtrackings}/fromlocation/${this.eParent.id}`
      const params = new URLSearchParams()
      params.append('page', this.page.toString())
      params.append('pageSize', this.pageSize.toString())

      const fromBase = this.parseFilterDate(this.filters.from)
      if (fromBase) {
        const from = new Date(fromBase.getTime())
        from.setHours(0, 0, 0, 0)
        params.append('fromDate', this.toApiDateTime(from))
      }
      const toBase = this.parseFilterDate(this.filters.to)
      if (toBase) {
        const to = new Date(toBase.getTime())
        to.setHours(23, 59, 59, 999)
        params.append('toDate', this.toApiDateTime(to))
      }
      if (this.cursorCreated) params.append('cursorCreated', this.cursorCreated)

      return `${baseUrl}?${params.toString()}`
    },
    fetchPage () {
      this.isLoading = true

      const url = this.buildFetchUrl()
      http({
        method: 'get',
        url
      }).then(response => {
        if (response.data.success === true) {
          const newItems = response.data.result || []

          if (this.page === 1) {
            this.items = newItems
          } else {
            this.items = this.items.concat(newItems)
          }

          this.hasMore = newItems.length === this.pageSize
          if (newItems.length > 0) {
            this.cursorCreated = newItems[newItems.length - 1].created
          } else {
            this.cursorCreated = null
          }
        }
      }).finally(() => {
        this.isLoading = false
      })
    },
    formatDate (dateString) {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return ''
      return new Intl.DateTimeFormat('de-CH', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).format(date)
    },
    resetAndFetch () {
      this.items = []
      this.page = 1
      this.cursorCreated = null
      this.hasMore = true
      this.fetchPage()
    },
    loadMore () {
      if (this.isLoading || !this.hasMore) return
      this.page += 1
      this.fetchPage()
    }
  },
  computed: {
    is () {
      return this.$store.getters[`${this.eCurrent.key}/is`]
    },
    definition () {
      return this.$store.getters[`${this.eCurrent.key}/definition`]
    },
    entities () {
      return this.$store.getters[`${this.eCurrent.key}/getUnwrapped`]
    }
  }
}
</script>

<style lang="scss" scoped>
.locations-uservisits {
  .uservisits-filter-card {
    max-width: 36rem;
    border: 1px solid rgba(0, 0, 0, 0.08);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

    ::v-deep .card-header {
      padding: 0.35rem 0.65rem;
      background: rgba(0, 0, 0, 0.02);
      border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    }
  }

  .uservisits-filter-card__title {
    font-size: 0.8125rem;
    font-weight: 600;
  }

  .uservisits-filter-label {
    font-size: 0.75rem;
    font-weight: 500;
    color: #495057;
    margin-bottom: 0.2rem;
  }

  .uservisits-filter-datepicker {
    max-width: 10.5rem;

    ::v-deep .b-form-btn-label-control.form-control {
      min-height: calc(1.5em + 0.5rem + 2px);
      font-size: 0.875rem;
      padding-top: 0.25rem;
      padding-bottom: 0.25rem;
    }
  }

  .uservisits-filter-row {
    @media (min-width: 576px) {
      flex-wrap: nowrap;
    }
  }

  .uservisits-filter-submit {
    @media (max-width: 575.98px) {
      width: 100%;
    }
  }
}
</style>
